import type React from "react";
import {useState} from "react";
import {Alert, Box, Button, InputAdornment, Skeleton, Stack, Switch, TextField, Typography} from "@mui/material";
import {useNavigate} from "react-router";
import ArrowIcon from "~/icons/svg/ArrowIcon.svg?react";
import {CustomCard} from "~/components/Card/Card";
import {COLORS} from "~/style/colors-ts";
import {notificationStore} from "~/stores/init";
import {PT_V2_STAFF_TABS, PT_V2_TABS, usePtV2Context} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Context.ts";
import type {PtV2TabKey} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";
import {
    usePtV2SettingsQuery,
    usePtV2UpdateSettingsMutation,
} from "~/experiments/pages/payment-terms-v2/usePaymentTermsV2Queries.ts";

/**
 * Payment Terms settings, for Mithra staff only: which tabs customers see in this environment, and the cost of capital
 * that values working capital a year (annual value = working capital × rate). Applies to every user
 * and every dataset here. The server refuses changes from anyone outside the `mithra_staff` group; the page does not
 * render for them either.
 */
export const PtV2SettingsPage: React.FC = () => {
    const {isStaff, tabPath} = usePtV2Context();
    const navigate = useNavigate();
    const {data: settings, isLoading, isError} = usePtV2SettingsQuery();
    const {setHiddenTabs, setCostOfCapital, isPending} = usePtV2UpdateSettingsMutation();
    const [rate, setRate] = useState<string | null>(null);  // the typed value; null shows the saved one

    const back = <Button size="small" variant="text" color="inherit" startIcon={<ArrowIcon fontSize="small"/>}
                         sx={{alignSelf: 'flex-start'}} onClick={() => navigate(tabPath('overview'))}>
        Payment Terms
    </Button>;

    if (!isStaff) {
        return <Stack gap={2}>{back}<Alert severity="warning">Payment Terms settings are for Mithra staff.</Alert></Stack>;
    }

    const hidden = settings?.hidden_tabs ?? [];
    const notify = (saving: Promise<unknown>) => saving
        .then(() => notificationStore.show('Settings saved.', {
            severity: 'success', id: 'pt-v2-settings-success', autoHideDuration: 3000,
        }))
        .catch(() => notificationStore.show('Could not save the settings. Please try again.', {
            severity: 'error', id: 'pt-v2-settings-error', autoHideDuration: 4000,
        }));
    const toggle = (tab: PtV2TabKey, show: boolean) => notify(setHiddenTabs(show ? hidden.filter(t => t !== tab) : [...hidden, tab]));

    const saved = settings?.cost_of_capital_pct ?? null;
    const typed = rate ?? (saved != null ? String(saved) : '');
    const parsed = typed.trim() === '' ? null : Number(typed.replace(',', '.'));
    const rateValid = parsed === null || (Number.isFinite(parsed) && parsed > 0 && parsed <= 100);
    const rateChanged = parsed !== saved;
    const saveRate = (value: number | null) => notify(setCostOfCapital(value).then(() => setRate(null)));

    return <Stack gap={2} sx={{maxWidth: 720}}>
        {back}
        <Box>
            <Typography component="h2" variant="h6" fontWeight={700}>Payment Terms settings</Typography>
            <Typography variant="body2" color="text.secondary">
                Mithra staff only. Applies to every user and every dataset in this environment.
            </Typography>
        </Box>
        {isError && <Alert severity="error">Could not load the settings.</Alert>}
        {isLoading && <Skeleton variant="rounded" height={300}/>}
        {settings && <CustomCard
            title="Cost of capital"
            subHeader="What freed or trapped working capital is worth a year: annual value = working capital × this rate. Shown next to the working-capital figures and used in savings initiatives. Leave empty to show no annual values.">
            <Stack direction="row" gap={1.5} alignItems="flex-start" flexWrap="wrap">
                <TextField size="small" label="Cost of capital" value={typed} sx={{width: 180}}
                           onChange={e => setRate(e.target.value)}
                           error={!rateValid} helperText={rateValid ? '% a year, e.g. 8.5' : 'Above 0 and at most 100'}
                           slotProps={{input: {endAdornment: <InputAdornment position="end">%</InputAdornment>},
                               htmlInput: {inputMode: 'decimal', 'aria-label': 'Cost of capital, percent a year'}}}/>
                <Button variant="contained" size="small" sx={{mt: 0.5}} disabled={!rateValid || !rateChanged || isPending}
                        onClick={() => saveRate(parsed)}>Save</Button>
                {saved != null && <Button size="small" sx={{mt: 0.5}} disabled={isPending} onClick={() => saveRate(null)}>Clear</Button>}
            </Stack>
        </CustomCard>}
        {settings && <CustomCard
            title="Tabs"
            subHeader="Hidden tabs disappear for customers; links to them open Overview instead. Mithra staff still see them, marked hidden.">
            <Stack>
                {PT_V2_TABS.map(({key, label}) => {
                    const hideable = settings.hideable_tabs.includes(key);
                    const note = key === 'overview' ? 'Always shown: it is the page Payment Terms opens on.'
                        : PT_V2_STAFF_TABS.includes(key) ? 'Mithra staff only: customers never see it.' : null;
                    return <Stack key={key} direction="row" alignItems="center" gap={1.5}
                                  sx={{py: 1, borderTop: 1, borderColor: COLORS.grey[150], '&:first-of-type': {borderTop: 'none'}}}>
                        <Box sx={{flex: 1, minWidth: 0}}>
                            <Typography variant="body2" fontWeight={500}>{label}</Typography>
                            {note && <Typography variant="caption" color="text.secondary">{note}</Typography>}
                        </Box>
                        <Switch checked={hideable ? !hidden.includes(key) : key === 'overview'}
                                disabled={!hideable || isPending}
                                onChange={(_e, checked) => toggle(key, checked)}
                                slotProps={{input: {'aria-label': `Show the ${label} tab to customers`}}}/>
                    </Stack>;
                })}
            </Stack>
        </CustomCard>}
    </Stack>;
};
